import { getAgentHarnessUri } from '../utils/EnvConfigUtils';
import { retrieveCachedUserDetails } from './session_thunks';
import {
	AgentAction,
	AgentActionOutcome,
	AgentConversationInfo,
	AgentHistoryPage,
	AgentModelsResponse,
	AgentSessionInfo,
	AgentUploadResponse,
} from '../types/agentHarness';

/**
 * Client for ow-agent-harness-service, the in-app AI assistant backend.
 *
 * This deliberately does NOT go through FetchUtils: the harness authenticates
 * with an httpOnly session cookie (credentials: 'include'), and the OpenWrench
 * X-Auth-Token must reach it exactly once — inside the login exchange body —
 * never as a header on every call. The harness holds the JWT server-side from
 * then on. Its origin is a sibling subdomain of the app, so the cookie is
 * same-site and third-party cookie blocking does not apply.
 */

const baseUrl = getAgentHarnessUri();

const readErrorMessage = (payload: any, fallback: string): string => {
	return payload && typeof payload.error === 'string' ? payload.error : fallback;
};

const agentRequest = (path: string, init: RequestInit = {}): Promise<Response> => {
	return fetch(baseUrl + path, { ...init, credentials: 'include' });
};

const agentJson = async (path: string, init: RequestInit = {}): Promise<any> => {
	const response = await agentRequest(path, init);
	let payload: any = null;
	try {
		payload = await response.json();
	} catch (parseError) {
		payload = null;
	}
	if (!response.ok) {
		const error: any = new Error(
			readErrorMessage(payload, 'The agent service returned an error (' + response.status + ')')
		);
		error.status = response.status;
		throw error;
	}
	return payload;
};

const postJson = (path: string, data: unknown): Promise<any> => {
	return agentJson(path, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(data),
	});
};

/**
 * The login currently in flight, if any — settled or not.
 *
 * Module-level because the two things that need to be ordered live in different
 * components: the chat hook exchanges the login, and `App` logs out. Signing out
 * while an exchange was still open sent the logout against no session at all,
 * and the exchange then installed a fresh authenticated cookie *after* sign-out.
 * That used to cost a session slot; since the fresh-boot path began reusing a
 * valid cookie, a session outliving its user is a much more serious thing.
 */
let inFlightLogin: Promise<unknown> | null = null;

/**
 * The exchange in flight, and what it is for.
 *
 * The chat hook coalesces logins too, but only within one mount. Expanding the
 * dock unmounts it and mounts the fullscreen route, so the second mount starts
 * with a fresh ref and exchanges again — two `Set-Cookie` responses racing, and
 * whichever lands last wins. The loser's conversation belongs to a session the
 * cookie no longer names, so its messages fail. Only a module-level record can
 * see across mounts.
 *
 * Keyed by token and role: a different account, or the same account in the
 * other role, is a genuinely different session and must not join this one.
 */
let activeExchange: { key: string; request: Promise<AgentSessionInfo> } | null = null;

/**
 * Exchange the web app's cached X-Auth-Token for a harness session cookie.
 * Call it before anything else, and again on any 401 — harness sessions are
 * in-memory server-side and do not survive its deploys.
 */
export const exchangeAgentLogin =
	(userType: string) =>
	(dispatch: any): Promise<AgentSessionInfo> => {
		const token = retrieveCachedUserDetails(['token']).token;
		if (!token) {
			return Promise.reject(new Error('Not signed in to OpenWrench'));
		}
		const key = userType + '\u0000' + token;
		if (activeExchange && activeExchange.key === key) {
			return activeExchange.request;
		}
		const request: Promise<AgentSessionInfo> = postJson('/api/login/token', {
			token,
			role: userType,
		});
		const settle = () => {
			// Only if it is still ours: a later exchange for a different identity
			// has already taken the slot and must not be cleared by this one.
			if (activeExchange && activeExchange.request === request) {
				activeExchange = null;
			}
		};
		activeExchange = { key, request };
		// Never rejects, so a failed login cannot make `agentLogout` throw.
		inFlightLogin = request.then(
			() => {
				settle();
			},
			() => {
				settle();
			}
		);
		return request;
	};

/**
 * Who the harness thinks it is talking to.
 *
 * The session cookie proves a session *works*, not whose it is. `/api/models`
 * cannot tell the difference, so reusing a live cookie on the strength of it
 * would let a new sign-in inherit the previous account's session — same browser,
 * previous JWT — and read and write as them.
 */
export const fetchAgentIdentity =
	() =>
	(dispatch: any): Promise<AgentSessionInfo> => {
		return agentJson('/api/me');
	};

export const fetchAgentModels =
	() =>
	(dispatch: any): Promise<AgentModelsResponse> => {
		return agentJson('/api/models');
	};

/** The signed-in person's saved conversations, newest first. */
export const fetchAgentHistory =
	() =>
	(dispatch: any): Promise<AgentHistoryPage> => {
		return agentJson('/api/history?limit=50');
	};

/**
 * One conversation by id. For a saved conversation this process no longer
 * holds, the harness rebuilds it from stored history first, so the answer's
 * status says whether it can be continued before the stream is opened.
 */
export const fetchAgentConversation =
	(conversationId: string) =>
	(dispatch: any): Promise<AgentConversationInfo> => {
		return agentJson('/api/conversations/' + encodeURIComponent(conversationId));
	};

export const createAgentConversation =
	(model?: string, thinkingLevel?: string) =>
	(dispatch: any): Promise<AgentConversationInfo> => {
		const body: { model?: string; thinkingLevel?: string } = {};
		if (model) {
			body.model = model;
		}
		if (thinkingLevel) {
			body.thinkingLevel = thinkingLevel;
		}
		return postJson('/api/conversations', body);
	};

/**
 * Kick off a turn. The reply arrives over the conversation's SSE stream, not
 * this response; `messageId` is client-minted so the stream's echo of the user
 * message is recognised instead of rendered twice.
 */
export const sendAgentMessage =
	(conversationId: string, messageId: string, text: string) =>
	(dispatch: any): Promise<void> => {
		return postJson('/api/conversations/' + encodeURIComponent(conversationId) + '/messages', {
			id: messageId,
			text,
		});
	};

export const cancelAgentTurn =
	(conversationId: string) =>
	(dispatch: any): Promise<void> => {
		return postJson('/api/conversations/' + encodeURIComponent(conversationId) + '/cancel', {});
	};

export const setAgentConversationModel =
	(conversationId: string, model: string) =>
	(dispatch: any): Promise<void> => {
		return postJson('/api/conversations/' + encodeURIComponent(conversationId) + '/model', {
			model,
		});
	};

export const setAgentThinkingLevel =
	(conversationId: string, thinkingLevel: string) =>
	(dispatch: any): Promise<void> => {
		return postJson('/api/conversations/' + encodeURIComponent(conversationId) + '/thinking', {
			thinkingLevel,
		});
	};

export const resolveAgentApproval =
	(approvalId: string, approved: boolean) =>
	(dispatch: any): Promise<void> => {
		return postJson('/api/approvals/' + encodeURIComponent(approvalId), { approved });
	};

/** Writes whose result was lost, for this login across all its conversations. */
export const fetchAgentActions =
	() =>
	(dispatch: any): Promise<AgentAction[]> => {
		return agentJson('/api/actions').then((payload) =>
			payload && Array.isArray(payload.actions) ? payload.actions : []
		);
	};

/** One action record, whatever its state — how a card answered elsewhere learns so. */
export const fetchAgentAction =
	(actionId: string) =>
	(dispatch: any): Promise<AgentAction> => {
		return agentJson('/api/actions/' + encodeURIComponent(actionId)).then(
			(payload) => payload.action
		);
	};

/**
 * Record what the user verified about an unknown outcome. This never runs the
 * operation; it only lifts the pause on that tool.
 */
export const reconcileAgentAction =
	(actionId: string, outcome: AgentActionOutcome, evidence: string) =>
	(dispatch: any): Promise<AgentAction> => {
		return postJson('/api/actions/' + encodeURIComponent(actionId) + '/reconcile', {
			confirmed: true,
			outcome,
			evidence,
		}).then((payload) => payload.action);
	};

/**
 * Upload an attachment for the agent to read. The returned server-side path is
 * what the agent's file tools resolve; reference it in the next message as
 * `[Uploaded file: <path>]` — the convention the harness's own UI established.
 */
export const uploadAgentFile =
	(file: File) =>
	(dispatch: any): Promise<AgentUploadResponse> => {
		return agentJson('/api/uploads?name=' + encodeURIComponent(file.name), {
			method: 'POST',
			body: file,
		});
	};

/**
 * Ends the harness session. Waits for any login still in flight first, so the
 * logout cannot be sent before the session it is meant to end exists.
 */
export const agentLogout =
	() =>
	async (dispatch: any): Promise<void> => {
		if (inFlightLogin) {
			await inFlightLogin;
		}
		return postJson('/api/logout', {});
	};

/** SSE stream URL for a conversation; open with `new EventSource(url, { withCredentials: true })`. */
export const getAgentStreamUrl = (conversationId: string): string => {
	return baseUrl + '/api/conversations/' + encodeURIComponent(conversationId) + '/events';
};

/**
 * Download URL for a file the agent saved (the `file` stream event's `url` is
 * relative).
 *
 * Applied once, in `useAgentChat`'s `file` handler, so every transcript entry
 * carries an absolute url. Do not call it again on `entry.url` — doing so
 * concatenated two absolute urls and produced an unusable href, which is why
 * downloads never worked.
 */
export const getAgentFileUrl = (relativeUrl: string): string => {
	return baseUrl + relativeUrl;
};

/**
 * The bytes of a file the agent saved.
 *
 * A plain `<a href>` would mostly work — the harness sends
 * `Content-Disposition: attachment`, so the browser downloads and stays put —
 * but only while the session holds. On a 401 it would instead navigate the tab
 * to a JSON error page and take the whole app with it, and an expired harness
 * session is an ordinary event here. Fetching keeps the failure in our hands;
 * the harness allows credentialed GETs from the app's origin.
 *
 * Takes the absolute url already on the transcript entry.
 */
const requestFileBlob = async (url: string): Promise<Blob> => {
	const response = await fetch(url, { credentials: 'include' });
	if (!response.ok) {
		const error: any = new Error('The file could not be downloaded (' + response.status + ')');
		error.status = response.status;
		throw error;
	}
	return response.blob();
};

/**
 * Fetches a saved file, re-authenticating once if the session has lapsed.
 *
 * Without the retry an expired session made the file permanently unreachable:
 * every press of the card's Retry repeated the same stale request, so the button
 * promised a recovery it could not deliver. Harness sessions expire on their own
 * TTL, so this is ordinary, not exceptional.
 */
export const downloadAgentFile =
	(url: string, userType: string) =>
	async (dispatch: any): Promise<Blob> => {
		try {
			return await requestFileBlob(url);
		} catch (fileError) {
			if (!fileError || (fileError.status !== 401 && fileError.status !== 403)) {
				throw fileError;
			}
		}
		await dispatch(exchangeAgentLogin(userType));
		return requestFileBlob(url);
	};
